import { useTranslation } from 'react-i18next';
import PageSizeSelector from '@/components/PageSizeSelector/PageSizeSelector';
import FundFilter from '@/components/filters/FundFilter/FundFilter';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import ExpirationStatusFilters from '@/components/filters/ExpirationStatusFilters/ExpirationStatusFilters';
import { ExpirationStatus } from '@/helpers/contactClassificationExpiration';
import { NO_CLASSIFICATION } from '@/models/in/ManagementSupportedDtoIn';
import IsraeliIdChangeFilter, { IsraeliIdChangeFilterProps } from '@/pages/Supporteds/IsraeliIdChange/IsraeliIdChangeFilter';
import styles from '@/components/filters/FiltersArea.module.scss';

interface FiltersAreaProps {
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  selectedFundId: number | null;
  onFundChange: (fundId: number | null) => void;
  /** Number of supporteds per classification name, as counted by the server. */
  classificationCounts: Record<string, number>;
  selectedClassifications: string[];
  onClassificationsChange: (classifications: string[]) => void;
  /** Number of classifications per expiration status, as counted by the server. */
  expirationCounts: Record<string, number>;
  selectedExpiration: ExpirationStatus | null;
  onExpirationChange: (expirationStatus: ExpirationStatus | null) => void;
  /** HAT-1428: the supporteds with a coordinator's ID correction request waiting - the server filters by it. */
  idChangeFilter?: IsraeliIdChangeFilterProps;
}

const FiltersArea = ({
  pageSize,
  onPageSizeChange,
  selectedFundId,
  onFundChange,
  classificationCounts,
  selectedClassifications,
  onClassificationsChange,
  expirationCounts,
  selectedExpiration,
  onExpirationChange,
  idChangeFilter,
}: FiltersAreaProps) => {
  const { t } = useTranslation();

  // Only classifications that actually exist in the current scope are offered, so the chips follow
  // the selected fund. Selected ones stay listed even if the count drops to zero. Sorted by the
  // label the user reads, with "no classification" last.
  const existingClassifications = Object.keys(classificationCounts)
    .filter((name) => classificationCounts[name] > 0 || selectedClassifications.includes(name))
    .sort((a, b) => {
      if (a === NO_CLASSIFICATION) return 1;
      if (b === NO_CLASSIFICATION) return -1;
      return t(a).localeCompare(t(b));
    });

  const handleClassificationChange = (classificationName: string) => {
    const isSelected = selectedClassifications.includes(classificationName);

    onClassificationsChange(
      isSelected
        ? selectedClassifications.filter((name) => name !== classificationName)
        : [...selectedClassifications, classificationName]
    );
  };

  const handleExpirationToggle = (expirationStatus: ExpirationStatus) => {
    onExpirationChange(selectedExpiration === expirationStatus ? null : expirationStatus);
  };

  return (
    <div className="flex flex-col gap-5">
      <ExpirationStatusFilters
        counts={expirationCounts}
        selectedExpiration={selectedExpiration}
        onToggle={handleExpirationToggle}
      />

      <div className={styles.filtersArea}>
        <div className={styles.filtersContainer}>
          {idChangeFilter && <IsraeliIdChangeFilter {...idChangeFilter} />}
          {existingClassifications.map((classificationName) => (
            <StatusFilter
              key={classificationName}
              title={classificationName === NO_CLASSIFICATION ? t('noClassification') : classificationName}
              isSelected={selectedClassifications.includes(classificationName)}
              onClick={() => handleClassificationChange(classificationName)}
              count={classificationCounts[classificationName] || 0}
            />
          ))}
          <FundFilter selectedFundId={selectedFundId} onFundChange={onFundChange} />
        </div>
        <div className={styles.controlsContainer}>
          <PageSizeSelector value={pageSize} onChange={onPageSizeChange} />
        </div>
      </div>
    </div>
  );
};

export default FiltersArea;
